export type PaymentErrorCode =
    | 'VALIDATION_ERROR'
    | 'API_ERROR'
    | 'PSP_ERROR'
    | 'NETWORK_ERROR'
    | 'POLLING_TIMEOUT'
    | 'PAYMENT_CANCELED'
    | 'PAYMENT_FAILED'
    | 'PAYMENT_INCOMPLETE'
    | 'GENERIC_ERROR';

export class PaymentError extends Error {
    public readonly traceId: string;
    public readonly httpStatus?: number;
    public readonly code: PaymentErrorCode;

    public paymentMethod?: string;

    constructor(
        message: string,
        code: PaymentErrorCode = 'GENERIC_ERROR',
        traceId?: string,
        httpStatus?: number
    ) {
        super(message);
        this.name = 'PaymentError';
        this.traceId = traceId || '';
        this.httpStatus = httpStatus;
        this.code = code;

        Object.setPrototypeOf(this, new.target.prototype);

        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, PaymentError);
        }
    }

    private static isNetworkError(error: Error): boolean {
        if (error.name !== 'TypeError') {
            return false;
        }
        const message = error.message.toLowerCase();
        return (
            message.includes('failed to fetch') ||
            message.includes('network') ||
            message.includes('load failed') ||
            message.includes('internet connection')
        );
    }

    private static hasMessage(
        error: unknown
    ): error is { message: string; traceId?: string; httpStatus?: number } {
        return (
            typeof error === 'object' &&
            error !== null &&
            'message' in error &&
            typeof error.message === 'string'
        );
    }

    public static fromUnknown(
        error: unknown,
        fallbackCode: PaymentErrorCode = 'GENERIC_ERROR',
        fallbackMessage = 'Unknown payment error occurred'
    ): PaymentError {
        if (error instanceof PaymentError) {
            return error;
        }

        if (error instanceof Error) {
            const errorCode = PaymentError.isNetworkError(error)
                ? 'NETWORK_ERROR'
                : fallbackCode;
            return new PaymentError(error.message, errorCode);
        }

        if (PaymentError.hasMessage(error)) {
            return new PaymentError(
                error.message,
                fallbackCode,
                error.traceId,
                error.httpStatus
            );
        }

        return new PaymentError(
            typeof error === 'string' ? error : fallbackMessage,
            fallbackCode
        );
    }
}
